import { useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Icon } from '@iconify-icon/react';
import { Button } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import { Turnstile, type TurnstileInstance } from '@marsidev/react-turnstile';
import Link from 'next/link';

import { ControlledTextarea, ControlledTextInput } from '@monorepo/components';

import { useContactUsForm } from '@/components/modules/Landing/api';
import { IContactUsRequest } from '@/types';
import { CONTACT_US_TYPES } from '@/types/entities/contact.entities';
import yup from '@/utils/yup';

const TEAM_SIZES = ['1–10', '11–50', '51–200', '201–1,000', '1,000+'] as const;

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'] as const;

const TIMES_OF_DAY = [
  { id: 'morning', label: 'Morning', hint: '9 AM – 12 PM' },
  { id: 'afternoon', label: 'Afternoon', hint: '12 – 4 PM' },
  { id: 'late', label: 'Late afternoon', hint: '4 – 6 PM' },
] as const;

const friendlyTimezone = (tz: string): string => {
  try {
    const part = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'long' })
      .formatToParts(new Date())
      .find((p) => p.type === 'timeZoneName')?.value;
    return part ? `${part} (${tz})` : tz;
  } catch {
    return tz;
  }
};

const demoFormSchema = yup.object({
  name: yup.string().trim().required('Your name keeps it personal.').max(80),
  email: yup
    .string()
    .trim()
    .required('A work email helps us prepare.')
    .email('That email doesn’t look right.'),
  company: yup.string().trim().required('Tell us where you work.').max(100),
  teamSize: yup
    .string()
    .trim()
    .required('Roughly, how big is the team?')
    .test('valid-team', 'Pick the closest match.', (val) =>
      val ? (TEAM_SIZES as readonly string[]).includes(val) : false
    ),
  availableDays: yup
    .array()
    .of(yup.string().required())
    .min(1, 'Pick at least one day that works.')
    .required(),
  availableTimeOfDay: yup
    .array()
    .of(yup.string().required())
    .min(1, 'Pick at least one time of day.')
    .required(),
  timezone: yup.string().trim().required(),
  useCase: yup
    .string()
    .trim()
    .required('A sentence is enough.')
    .min(10, 'A sentence is enough — what do you wish you could ask an agent?')
    // The API caps the composed message at 2000 chars; leave room for the company/availability lines.
    .max(1500, 'Please keep it under 1,500 characters.'),
});

type DemoFormSchema = yup.InferType<typeof demoFormSchema>;

const defaults: DemoFormSchema = {
  name: '',
  email: '',
  company: '',
  teamSize: '',
  availableDays: [],
  availableTimeOfDay: [],
  timezone: '',
  useCase: '',
};

/* ── Component ── */
interface DemoFormProps {
  variant?: 'hero' | 'inline';
}

const DemoForm: React.FC<DemoFormProps> = ({ variant = 'hero' }) => {
  const [submitted, setSubmitted] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileInstance>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const successRef = useRef<HTMLDivElement>(null);
  const contactMutation = useContactUsForm({ suppressErrorToast: true });

  const methods = useForm<DemoFormSchema>({
    resolver: yupResolver(demoFormSchema),
    defaultValues: defaults,
    mode: 'onBlur',
  });
  const { handleSubmit, watch, setValue, getValues, formState } = methods;
  const teamSize = watch('teamSize');
  const availableDays = watch('availableDays') ?? [];
  const availableTimeOfDay = watch('availableTimeOfDay') ?? [];
  const timezone = watch('timezone');

  // Auto-detect once so sales gets a usable timezone with the request.
  useEffect(() => {
    if (timezone) return;
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (tz) setValue('timezone', tz, { shouldDirty: false, shouldValidate: false });
    } catch {
      // Leave empty; validation will catch the rare browser/runtime miss.
    }
  }, [timezone, setValue]);

  const toggleInArray = (field: 'availableDays' | 'availableTimeOfDay', value: string) => {
    const current = (getValues(field) ?? []) as string[];
    const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
    setValue(field, next, { shouldDirty: true, shouldValidate: true });
  };

  // The success card is far shorter than the form, so on single-column layouts the page
  // collapses and the card ends up above the viewport. Bring it back into view.
  useEffect(() => {
    if (!submitted) return;
    successRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    successRef.current?.focus({ preventScroll: true });
  }, [submitted]);

  const isLoading = contactMutation.isPending;
  const turnstileEnabled = !!process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY;
  const submitDisabled = useMemo(
    () => isLoading || (turnstileEnabled && !turnstileToken),
    [isLoading, turnstileEnabled, turnstileToken]
  );

  const onSubmit = async (data: DemoFormSchema) => {
    if (turnstileEnabled && !turnstileToken) {
      showNotification({ message: 'Please complete the verification check.', color: 'red' });
      return;
    }

    const tod = (data.availableTimeOfDay ?? [])
      .map((id) => TIMES_OF_DAY.find((t) => t.id === id))
      .filter((x): x is (typeof TIMES_OF_DAY)[number] => Boolean(x))
      .map((t) => `${t.label} (${t.hint})`)
      .join(', ');

    const composedMessage =
      `Demo request — ${data.company} (team ${data.teamSize}).\n\n` +
      `What they want to see:\n${data.useCase}\n\n` +
      `Availability:\n` +
      `- Days: ${(data.availableDays ?? []).join(', ')}\n` +
      `- Time of day: ${tod}\n` +
      `- Timezone: ${data.timezone}`;

    const payload: IContactUsRequest = {
      name: data.name,
      email: data.email,
      companyName: data.company,
      message: composedMessage,
      type: CONTACT_US_TYPES.PLAN,
      ...(turnstileToken ? { turnstileToken } : {}),
    };

    try {
      await contactMutation.mutateAsync(payload);
      setSubmitted(true);
      showNotification({
        title: 'Request received',
        message: 'We will email you within one business day to confirm your demo slot.',
        color: 'green',
      });
      methods.reset(defaults);
      setTurnstileToken(null);
      turnstileRef.current?.reset();
    } catch (err) {
      showNotification({
        message:
          err instanceof Error && err.message ? err.message : 'Something went wrong. Try again?',
        color: 'red',
      });
      // Turnstile tokens are single-use: a retry with the spent token would be rejected.
      setTurnstileToken(null);
      turnstileRef.current?.reset();
    }
  };

  // `noValidate` + mode 'onBlur' means a failed submit used to do nothing visible when the
  // offending field (e.g. Team size) was scrolled out of view.
  const onInvalid = () => {
    showNotification({ message: 'Please fill in the highlighted fields.', color: 'red' });
    // Errors render on the next commit, so look for them after the DOM has updated.
    requestAnimationFrame(() => {
      const firstError = formRef.current?.querySelector<HTMLElement>(
        '[aria-invalid="true"], [data-demo-field-error]'
      );
      firstError?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    });
  };

  const cardClass =
    variant === 'hero'
      ? 'relative rounded-[28px] bg-white p-7 sm:p-8 shadow-[0_24px_70px_-20px_rgba(0,71,144,0.28),0_8px_24px_-8px_rgba(13,27,62,0.12)] ring-1 ring-black/[0.04]'
      : 'relative rounded-3xl bg-white p-7 sm:p-9 shadow-xl ring-1 ring-black/5';

  if (submitted) {
    return (
      <div ref={successRef} tabIndex={-1} role="status" aria-live="polite" className={cardClass}>
        <div className="flex flex-col items-center text-center">
          <div
            className="flex h-14 w-14 items-center justify-center rounded-full"
            style={{ background: '#dcfce7' }}>
            <Icon icon="solar:check-circle-bold" className="text-[28px] text-[#16a34a]" />
          </div>
          <h3
            className="mt-5 text-[26px] leading-[1.1] tracking-tight text-[#0a1024]"
            style={{ fontFamily: 'Fraunces, serif', fontWeight: 500 }}>
            Got it — request received.
          </h3>
          <p className="mt-3 max-w-sm text-[15px] leading-relaxed text-slate-500">
            We have your availability. A real human will email you within one business day to
            confirm a 25-minute slot from the windows you picked. In the meantime — pour a coffee,
            read a citation, breathe.
          </p>
          <button
            type="button"
            onClick={() => setSubmitted(false)}
            className="mt-6 text-xs font-semibold uppercase tracking-[0.18em] text-[#004790] hover:text-[#003366]">
            Submit another request →
          </button>
        </div>
      </div>
    );
  }

  return (
    <FormProvider {...methods}>
      <form
        ref={formRef}
        onSubmit={handleSubmit(onSubmit, onInvalid)}
        className={cardClass}
        noValidate
        aria-label="Book a Chat.co demo">
        {/* Live indicator */}
        <div className="absolute -top-3 left-7 flex items-center gap-2 rounded-full bg-[#0a1024] px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-white shadow-lg">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-70" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-emerald-400" />
          </span>
          Booking now
        </div>

        <header className="mb-6">
          <h2
            className="text-[24px] leading-[1.1] tracking-tight text-[#0a1024] sm:text-[28px]"
            style={{ fontFamily: 'Fraunces, serif', fontWeight: 500 }}>
            Pick a 25-minute window.
          </h2>
          <p className="mt-2 text-[14px] leading-relaxed text-slate-500">
            We&apos;ll build an agent on your docs while you watch. No pitch deck.
          </p>
        </header>

        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <ControlledTextInput
              name="name"
              control={methods.control}
              label="Your name"
              placeholder="Ada Lovelace"
              required
            />
            <ControlledTextInput
              name="email"
              control={methods.control}
              type="email"
              label="Work email"
              placeholder="ada@yourco.com"
              required
            />
          </div>

          <ControlledTextInput
            name="company"
            control={methods.control}
            label="Company"
            placeholder="The interesting one you work for"
            required
          />

          <div>
            <label className="mb-2 block text-sm font-semibold text-[#0a1024]">
              Team size <span className="text-[#c08838]">*</span>
            </label>
            <div className="flex flex-wrap gap-1.5">
              {TEAM_SIZES.map((size) => {
                const active = teamSize === size;
                return (
                  <button
                    key={size}
                    type="button"
                    onClick={() =>
                      setValue('teamSize', size, { shouldDirty: true, shouldValidate: true })
                    }
                    className={[
                      'rounded-full border px-3.5 py-1.5 text-[12px] font-semibold transition-all duration-150',
                      active
                        ? 'border-[#004790] bg-[#004790] text-white shadow-sm shadow-blue-500/25'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50',
                    ].join(' ')}>
                    {size}
                  </button>
                );
              })}
            </div>
            {formState.errors.teamSize && (
              <p data-demo-field-error className="mt-1.5 text-xs text-red-500">
                {formState.errors.teamSize.message}
              </p>
            )}
          </div>

          <div>
            <div className="mb-2 flex items-baseline justify-between">
              <label className="block text-sm font-semibold text-[#0a1024]">
                Days that work <span className="text-[#c08838]">*</span>
              </label>
              <span className="text-[11px] text-slate-400">Pick all that apply</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {DAYS.map((day) => {
                const active = availableDays.includes(day);
                return (
                  <button
                    key={day}
                    type="button"
                    aria-pressed={active}
                    onClick={() => toggleInArray('availableDays', day)}
                    className={[
                      'rounded-full border px-3.5 py-1.5 text-[12px] font-semibold transition-all duration-150',
                      active
                        ? 'border-[#004790] bg-[#004790] text-white shadow-sm shadow-blue-500/25'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50',
                    ].join(' ')}>
                    {day}
                  </button>
                );
              })}
            </div>
            {formState.errors.availableDays && (
              <p data-demo-field-error className="mt-1.5 text-xs text-red-500">
                {formState.errors.availableDays.message as string}
              </p>
            )}
          </div>

          <div>
            <div className="mb-2 flex items-baseline justify-between">
              <label className="block text-sm font-semibold text-[#0a1024]">
                Time of day <span className="text-[#c08838]">*</span>
              </label>
              <span className="text-[11px] text-slate-400">Pick all that apply</span>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {TIMES_OF_DAY.map((t) => {
                const active = availableTimeOfDay.includes(t.id);
                return (
                  <button
                    key={t.id}
                    type="button"
                    aria-pressed={active}
                    onClick={() => toggleInArray('availableTimeOfDay', t.id)}
                    className={[
                      'group flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-[12px] font-semibold transition-all duration-150',
                      active
                        ? 'border-[#004790] bg-[#004790] text-white shadow-sm shadow-blue-500/25'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50',
                    ].join(' ')}>
                    {t.label}
                    <span
                      className={[
                        'text-[10px] font-normal',
                        active ? 'text-white/70' : 'text-slate-400',
                      ].join(' ')}>
                      · {t.hint}
                    </span>
                  </button>
                );
              })}
            </div>
            {formState.errors.availableTimeOfDay && (
              <p data-demo-field-error className="mt-1.5 text-xs text-red-500">
                {formState.errors.availableTimeOfDay.message as string}
              </p>
            )}
            {timezone && (
              <p className="mt-2 flex items-center gap-1.5 text-[11px] text-slate-400">
                <Icon icon="solar:global-bold" className="text-[13px] text-slate-400" />
                We&apos;ll use{' '}
                <span className="font-medium text-slate-500">{friendlyTimezone(timezone)}</span>.
                Looks off? Mention it in the box below.
              </p>
            )}
          </div>

          <ControlledTextarea
            name="useCase"
            control={methods.control}
            label="What do you wish you could ask an agent?"
            placeholder="e.g. ‘Which clause in the MSA covers data residency for our EU customers?’"
            minRows={3}
            required
            // Without a form context ControlledTextarea cannot read its own error, so render it here.
            errorEnabled={false}
          />
          {formState.errors.useCase && (
            <p data-demo-field-error className="-mt-2 text-xs text-red-500">
              {formState.errors.useCase.message}
            </p>
          )}

          {turnstileEnabled && (
            <Turnstile
              ref={turnstileRef}
              siteKey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY as string}
              onSuccess={setTurnstileToken}
              onError={() => setTurnstileToken(null)}
              onExpire={() => setTurnstileToken(null)}
            />
          )}

          <Button
            type="submit"
            fullWidth
            size="lg"
            loading={isLoading}
            disabled={submitDisabled}
            className="group !rounded-xl !bg-[#0a1024] !px-6 !py-3 !text-[14px] !font-semibold !text-white !shadow-lg !shadow-[#0d1b3e]/20 transition-all duration-200 hover:!bg-[#0d1b3e] hover:!shadow-xl hover:!shadow-[#0d1b3e]/30">
            Book my live demo
            <Icon
              icon="tabler:arrow-right"
              className="ml-1.5 inline-block text-[16px] transition-transform duration-200 group-hover:translate-x-0.5"
            />
          </Button>

          <p className="text-center text-[11px] leading-relaxed text-slate-400">
            By submitting you agree to our{' '}
            <Link
              href="/privacy-policy"
              className="underline-offset-2 hover:text-slate-600 hover:underline">
              privacy policy
            </Link>
            . No spam, no recurring nags — we promise.
          </p>
        </div>
      </form>
    </FormProvider>
  );
};

export default DemoForm;
